import type { AdminTwin, CameraSetup, PositionSource, TimezoneSource } from '@repo/frontend-types';
import { isUsableOrigin } from './resolveActivationOrigin';
import { nameForCreate } from './twinCreateForm';

/**
 * The Twin details step's pure half — the form shape, its validation, its dirty check and the
 * PATCH payloads it builds, one per kind of change (`twin-details-page-address`, R6).
 *
 * Separated from the component so the rules that decide what reaches the twin record are testable
 * without a DOM, and so the payload's wire quirks (§ the builders below) are stated in one
 * place rather than inferred from JSX.
 */

/** Every field the step edits, held as strings — a form's native currency. */
export interface TwinDetailsForm {
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  latitude: string;
  longitude: string;
  iana_timezone: string;
}

export type TwinDetailsField = keyof TwinDetailsForm;

export const TWIN_DETAILS_FIELDS: TwinDetailsField[] = [
  'address_1',
  'address_2',
  'city',
  'state',
  'postcode',
  'country',
  'latitude',
  'longitude',
  'iana_timezone',
];

/** The address lines, in the order a geocoder wants them. */
export const ADDRESS_FIELDS = [
  'address_1',
  'address_2',
  'city',
  'state',
  'postcode',
] as const satisfies readonly TwinDetailsField[];

const str = (value: unknown): string => (value == null ? '' : String(value));

/**
 * The twin record → the form.
 *
 * Every field becomes `''` when absent rather than `undefined`, so the inputs stay controlled and
 * the dirty check below compares like with like.
 */
export const formFromTwin = (twin: AdminTwin | null): TwinDetailsForm => ({
  address_1: str(twin?.address_1),
  address_2: str(twin?.address_2),
  city: str(twin?.city),
  state: str(twin?.state),
  postcode: str(twin?.postcode),
  country: str(twin?.country),
  latitude: str(twin?.latitude),
  longitude: str(twin?.longitude),
  iana_timezone: str(twin?.iana_timezone),
});

export type TwinDetailsErrors = Partial<Record<TwinDetailsField, string>>;

const LAT_RANGE = 90;
const LNG_RANGE = 180;

/**
 * Field-level validation: latitude within ±90, longitude within ±180 and orientation within ±360,
 * the ranges Edit mode's Twin tab enforced before it was retired.
 *
 * Deliberately permissive about EMPTINESS: partial completion is allowed (the wizard is resumable
 * by design), so a blank address or a blank position saves happily. What is not allowed is a value
 * that is present and wrong, or half a coordinate pair — see the note on the pair below.
 */
export const validateTwinDetails = (form: TwinDetailsForm): TwinDetailsErrors => {
  const errors: TwinDetailsErrors = {};

  const numeric = (field: TwinDetailsField, label: string): number | null => {
    const raw = form[field].trim();
    if (raw === '') return null;
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      errors[field] = `${label} must be a number.`;
      return null;
    }
    return value;
  };

  const latitude = numeric('latitude', 'Latitude');
  const longitude = numeric('longitude', 'Longitude');

  if (latitude != null && (latitude < -LAT_RANGE || latitude > LAT_RANGE)) {
    errors.latitude = 'Latitude must be between -90 and 90 degrees.';
  }
  if (longitude != null && (longitude < -LNG_RANGE || longitude > LNG_RANGE)) {
    errors.longitude = 'Longitude must be between -180 and 180 degrees.';
  }

  // Half a pair is never savable. Two reasons, and the second is the load-bearing one: a lone
  // value tells us nothing about where the twin is, AND the middleware only propagates
  // `mapOrigin` to dGraph when BOTH arrive in the same patch — so a half pair would persist to
  // AdminDB and silently skip the twin entity, leaving the two stores disagreeing.
  const latFilled = form.latitude.trim() !== '';
  const lngFilled = form.longitude.trim() !== '';
  if (latFilled !== lngFilled) {
    const missing: TwinDetailsField = latFilled ? 'longitude' : 'latitude';
    errors[missing] = 'Latitude and longitude go together — enter both, or neither.';
  }

  return errors;
};

/** Which fields the user has changed since the record was loaded. */
export const changedFields = (
  form: TwinDetailsForm,
  original: TwinDetailsForm,
): TwinDetailsField[] =>
  // Trimmed on BOTH sides. A record that already holds untrimmed text (one live config row stores
  // an untrimmed '#FFFFFF\n', so this is not hypothetical) would otherwise read as dirty the
  // moment the form loaded, and every save would re-send every field.
  TWIN_DETAILS_FIELDS.filter(field => form[field].trim() !== original[field].trim());

export const isDirty = (form: TwinDetailsForm, original: TwinDetailsForm): boolean =>
  changedFields(form, original).length > 0;

/**
 * Does this form give Go Live somewhere to put the twin?
 *
 * The SAME predicate the activation chain gates on, deliberately — so the step's completion tick
 * can never say "done" while Go Live would refuse.
 */
/** The coordinate pair alone — all the position predicates read. */
export type TwinPosition = Pick<TwinDetailsForm, 'latitude' | 'longitude'>;

export const isLocated = (form: TwinPosition): boolean =>
  isUsableOrigin(form.latitude.trim(), form.longitude.trim());

/**
 * The legacy "unset position" placeholder — a point in the Thames estuary near Erith.
 *
 * FOUND IN THE REAL DATA: **11 of staging's 24 twins** carried exactly `51.4779 / 0` and none
 * `NULL` or `0,0` (checked 2026-07-31); 5 of 36 on each database on 2026-10-17. The middleware wrote
 * this pair on every create sent without coordinates, from 2025-04-22 (`1d31686c6`) until
 * `twin-details-page-create-rules` removed it; a twin created since has no position. Edit mode's
 * Twin tab, whose postcode lookup also treated it as unset, was retired
 * (`retire-edit-mode-teardown`).
 *
 * It is deliberately **NOT** rejected by `isUsableOrigin`, and therefore never blocks activation.
 * A real coordinate is a real coordinate: a customer with a site at that point must be able to go
 * live, and hard-coding one magic pair into a correctness gate is a booby trap that fires on
 * legitimate data. But a twin sitting on it is almost certainly unpositioned, so the step SAYS so
 * and lets the user decide — a prompt, not a refusal.
 */
export const PLACEHOLDER_ORIGIN = { latitude: 51.4779, longitude: 0 } as const;

export const looksLikePlaceholderOrigin = (form: TwinPosition): boolean =>
  Number(form.latitude.trim()) === PLACEHOLDER_ORIGIN.latitude &&
  Number(form.longitude.trim()) === PLACEHOLDER_ORIGIN.longitude &&
  form.latitude.trim() !== '' &&
  form.longitude.trim() !== '';

/**
 * The single-line address the Launch summary shows, or null. One definition, read by the step's
 * report and by the page's own seed from its twin read (`twin-details-page-landing`, R3), so the two
 * can never word the same twin differently.
 */
export const summaryAddress = (form: TwinDetailsForm): string | null =>
  [form.address_1, form.city, form.postcode]
    .map(part => part.trim())
    .filter(Boolean)
    .join(', ') || null;

/**
 * The bound on the twin record's requests — the step's read and save, and the page's read alike.
 * Axios has NO default timeout, so a hung middleware would otherwise leave the step's skeleton, its
 * spinning Save, or the page's *Opening the twin…* up for ever.
 */
export const TWIN_REQUEST_TIMEOUT_MS = 20000;

/**
 * What `updateTwin` accepts. Narrower than the DTO — only the fields this step owns. The step no
 * longer reads or writes `altitude`, which has no screen since nothing reads it (epic
 * retire-edit-mode decision).
 */
export interface TwinDetailsPatch {
  /** The twin's name (`twin-details-page-summary`, R3.5), sent as `nameForCreate` gives it. */
  name?: string;
  /**
   * The twin's description (`retire-edit-mode-twin-description`, R3), trimmed. `null` clears it; the
   * step never sends `''`.
   */
  description?: string | null;
  address_1?: string;
  address_2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
  /** `null` with `position_source: null` clears the position (R5.3: both blank is no position). */
  latitude?: string | null;
  longitude?: string | null;
  position_source?: PositionSource | null;
  iana_timezone?: string;
  timezone_source?: TimezoneSource;
  /**
   * Degrees, sent as the typed string (`retire-edit-mode-position-write-path`, R2). It has no source
   * column, so it travels alone.
   */
  orientation?: string;
  /**
   * The view the twin viewer opens on (`retire-edit-mode-opening-view`, R7): all five keys, rounded,
   * written whole by Position on map's *Use this view*.
   */
  camera_start_args?: CameraSetup;
}

/**
 * The builders below are the only way the step makes a `PATCH /api/twins/:bid` body. Three wire
 * rules live here, checked against `twins.dto.ts` (`UpdateTwinDto`) and `twin-value-sources.ts`:
 *
 * 1. **`latitude`/`longitude` go as STRINGS** — the form's own text, trimmed. The columns are
 *    numeric; the route accepts a number or a numeric string for each (`IsNumericValue`) and
 *    PostgREST casts on the way in. A cleared pair goes as `null`: `IsNumericValue` refuses `''`.
 * 2. **A value travels with its source.** The route refuses a source sent without its value, and
 *    clears the source when a value arrives without one — so the pair and `position_source`, and
 *    the zone and `timezone_source`, are only ever built together (a cleared pair names its
 *    source as `null`), and a merge of two patches (`useTwinDetailsSave`) can never split them.
 * 3. **Lat/lng always travel TOGETHER.** The middleware only propagates `mapOrigin` to dGraph when
 *    both keys are present, so half a pair would persist to AdminDB and skip the twin entity.
 *
 * The route refuses any key it cannot write, and a twin's BID, type and organisation whatever their
 * value (feature fixed-identity), so no builder names anything else. A `name` is held to the create
 * rules (`checkedRename`): a rule it breaks is a 400 and a name another twin of the organisation
 * has is a 409, each naming `name` in `validationErrors` (`useTwinDetailsSave` drops just the name).
 * A `description` is free text the route never refuses; a cleared one goes as `null`.
 */

/**
 * A rename (`twin-details-page-summary`, R3.4), or `null` when there is nothing to send: a blank
 * name, or one that is the saved name once both are put through `nameForCreate` — so a stray space
 * or a pasted line break never sends the same name again.
 */
export const namePatch = (typed: string, savedName: string | null): TwinDetailsPatch | null => {
  const wanted = nameForCreate(typed);
  if (wanted === '' || wanted === nameForCreate(savedName ?? '')) return null;
  return { name: wanted };
};

/**
 * A description (`retire-edit-mode-twin-description`, R3), or `null` when there is nothing to send:
 * the typed text is the saved description once both are trimmed. Optional, so a blank or
 * whitespace-only description is sent as `null`, which clears it. Line breaks inside are kept.
 */
export const descriptionPatch = (
  typed: string,
  savedDescription: string | null,
): TwinDetailsPatch | null => {
  const wanted = typed.trim();
  if (wanted === (savedDescription ?? '').trim()) return null;
  return { description: wanted === '' ? null : wanted };
};

/** What the description field opens with: a blank or whitespace-only description opens empty. */
export const descriptionFieldText = (description: string | null): string =>
  description?.trim() ? description : '';

/**
 * Whether an accepted save changed the address: a line or the country. Only these start the address
 * lookup (`onboarding-persistent-scene-twin-details-map`, R3.1); a name, description, hours or
 * position save does not.
 */
export const carriesAddress = (patch: TwinDetailsPatch): boolean =>
  ADDRESS_FIELDS.some(field => field in patch) || 'country' in patch;

/** A chosen country, as its upper-case code, or `''` when the field is cleared (R2.2, R2.6). */
export const countryPatch = (code: string | null): TwinDetailsPatch => ({ country: code ?? '' });

/** One typed address line, trimmed (R3.2). */
export const linePatch = (
  field: (typeof ADDRESS_FIELDS)[number],
  value: string,
): TwinDetailsPatch => ({ [field]: value.trim() }) as TwinDetailsPatch;

/**
 * The coordinate pair with its source, or `null` when the pair is not savable: half a pair, a
 * non-number or a value out of range (R5.3). A `null` is never sent; the field errors say why.
 */
export const positionPatch = (
  form: Pick<TwinDetailsForm, 'latitude' | 'longitude'>,
  source: PositionSource,
): TwinDetailsPatch | null => {
  const latitude = form.latitude.trim();
  const longitude = form.longitude.trim();
  const errors = validateTwinDetails({ ...formFromTwin(null), latitude, longitude });
  if (errors.latitude || errors.longitude) return null;
  // Both blank is "no position": nulls, since the route refuses `''`, and a `null` source, so a merge
  // over an earlier pair cannot keep that pair's source.
  if (latitude === '' && longitude === '') {
    return { latitude: null, longitude: null, position_source: null };
  }
  return { latitude, longitude, position_source: source };
};

const ORIENTATION_RANGE = 360;

export const ORIENTATION_ERROR = 'Enter an orientation from -360 to 360';

/**
 * Why a typed orientation cannot be saved, or `undefined` when it can
 * (`retire-edit-mode-position-write-path`, Inputs). A finite number from -360 to 360 inclusive. Blank
 * over a blank record is no change; blank over a stored value is refused, because Edit mode made it
 * required and the renderers read it as a number, so it is never cleared.
 */
export const orientationError = (typed: string, saved: string): string | undefined => {
  const raw = typed.trim();
  if (raw === '') return saved.trim() === '' ? undefined : ORIENTATION_ERROR;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < -ORIENTATION_RANGE || value > ORIENTATION_RANGE) {
    return ORIENTATION_ERROR;
  }
  return undefined;
};

/**
 * A typed orientation, or `null` when there is nothing to send: unchanged (trimmed on both sides) or
 * not savable, in which case `orientationError` says why.
 */
export const orientationPatch = (typed: string, saved: string): TwinDetailsPatch | null => {
  const raw = typed.trim();
  if (raw === saved.trim() || orientationError(typed, saved)) return null;
  return { orientation: raw };
};

/** A zone with where it came from (R4.3, R4.5, R4.6). Never blank. */
export const timezonePatch = (zone: string, source: TimezoneSource): TwinDetailsPatch => ({
  iana_timezone: zone,
  timezone_source: source,
});

/**
 * The twin record with a saved patch applied — every key the patch carries, so the page's copy of
 * the twin agrees with what was saved whichever step saved it (`retire-edit-mode-position-write-path`,
 * R6). Lat/lng/orientation are stored as the patch sent them (strings, or `null` for a cleared pair),
 * the shape `getTwinById` answers with.
 */
export const applyTwinPatch = (twin: AdminTwin, patch: TwinDetailsPatch): AdminTwin => {
  const next = { ...twin } as unknown as Record<string, unknown>;
  for (const [key, value] of Object.entries(patch)) {
    if (value !== undefined) next[key] = value;
  }
  // The route stores the typed orientation as a number (`1e2` is 100), and the page's readers parse
  // the cached string, so it is cached as the number stored (Copilot review). The PATCH still sends
  // the typed string.
  if (patch.orientation !== undefined && Number.isFinite(Number(patch.orientation))) {
    next.orientation = String(Number(patch.orientation));
  }
  return next as unknown as AdminTwin;
};

/** The page's twin record after a save for `bid`: a patch for another twin changes nothing. */
export const recordAfterSave = (
  record: AdminTwin | null,
  bid: string,
  patch: TwinDetailsPatch,
): AdminTwin | null => (record && record.bid === bid ? applyTwinPatch(record, patch) : record);

/** The patch → the form fields it sets, trimmed — what the step re-baselines to after a save. */
export const formFieldsOf = (patch: TwinDetailsPatch): Partial<TwinDetailsForm> => {
  const out: Partial<TwinDetailsForm> = {};
  for (const field of TWIN_DETAILS_FIELDS) {
    const value = patch[field];
    if (typeof value === 'string') out[field] = value.trim();
    else if (value === null) out[field] = '';
  }
  return out;
};

/**
 * What each field is called in the clash sentence (`onboarding-multiplayer-twin-record-checks`,
 * R3.4). A value and its source share a label, and the address fields are one *address*. In this
 * order, so several read naturally: *The name and the address*.
 */
const CLASH_LABELS: ReadonlyArray<[string, ReadonlyArray<string>]> = [
  ['the name', ['name']],
  ['the description', ['description']],
  ['the address', ['address_1', 'address_2', 'city', 'state', 'postcode']],
  ['the country', ['country']],
  ['the timezone', ['iana_timezone', 'timezone_source']],
  ['the position', ['latitude', 'longitude', 'position_source']],
  ['the orientation', ['orientation']],
];

/** `a`, `a and b`, `a, b and c`. */
const joinLabels = (labels: string[]): string =>
  labels.length <= 1
    ? (labels[0] ?? '')
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

/**
 * The sentence the save tag shows when a save was refused because someone else changed a field
 * (R3.4): *The name was changed by someone else at 14:32. It now shows their change.* `modifiedAt` is
 * the server's ISO instant; the time is on the operator's clock, as the tag's other times are. With
 * no time, the sentence says only that it changed. A field with no label is *a detail*.
 */
export const clashSentence = (fields: string[], modifiedAt: string | null): string => {
  const named = new Set(fields);
  const labels = CLASH_LABELS.filter(([, keys]) => keys.some(key => named.has(key))).map(
    ([label]) => label,
  );
  const what = labels.length > 0 ? joinLabels(labels) : 'a detail';
  const at = modifiedAt ? new Date(modifiedAt) : null;
  const when =
    at && !Number.isNaN(at.getTime())
      ? ` at ${at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}`
      : '';
  // Several things *were* changed (the spec's pattern says *was*; read as the singular case).
  const verb = labels.length > 1 ? 'were' : 'was';
  return `${what.charAt(0).toUpperCase()}${what.slice(1)} ${verb} changed by someone else${when}. It now shows their change.`;
};
